import { useMemo, useRef } from 'react';
import { Download, FileUp, Loader2, Play, Trash2, Upload } from 'lucide-react';
import type {
  CameraAction as ProtoCameraAction,
  Command,
  VehicleAction as ProtoVehicleAction,
} from '@fabricos/protocol-ts';
import { Button } from '@/components/ui/Button';
import { sendCommand } from '@/hooks/useHubSocket';
import { useMission, type Waypoint } from '@/store/mission';
import { useMissionUpload } from '@/store/missionUpload';
import { useSelection } from '@/store/selection';
import { useToasts } from '@/store/toasts';
import { log } from '@/lib/logger';
import {
  downloadText,
  fromGeoJSON,
  fromKML,
  toGeoJSON,
  toKML,
} from '@/mission/format';

function makeId(): string {
  return `cmd-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
}

// UI uses Bramer-style SCREAMING_SNAKE strings; the protocol uses snake_case
// per the Rust enum serde rename. These mappers bridge the two.
function mapCameraAction(a: string): ProtoCameraAction | null {
  switch (a) {
    case 'TAKE_PHOTO':
      return 'take_photo';
    case 'START_PHOTO_INTERVAL':
      return 'start_photo_interval';
    case 'STOP_PHOTO_INTERVAL':
      return 'stop_photo_interval';
    case 'START_PHOTO_DISTANCE':
      return 'start_photo_distance';
    case 'STOP_PHOTO_DISTANCE':
      return 'stop_photo_distance';
    default:
      return null;
  }
}
function mapVehicleAction(a: string): ProtoVehicleAction | null {
  switch (a) {
    case 'TAKEOFF':
      return 'takeoff';
    case 'LAND':
      return 'land';
    case 'TRANSITION_TO_FW':
      return 'transition_to_fw';
    case 'TRANSITION_TO_MC':
      return 'transition_to_mc';
    default:
      return null;
  }
}

/** Great-circle distance, metres. Cheap and good enough for mission length. */
function distM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6_371_000;
  const φ1 = (a.lat * Math.PI) / 180;
  const φ2 = (b.lat * Math.PI) / 180;
  const dφ = ((b.lat - a.lat) * Math.PI) / 180;
  const dλ = ((b.lon - a.lon) * Math.PI) / 180;
  const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Mission controls dock — lives in the LEFT sidebar bottom when map mode is
 * Waypoint. Owns mission name, save/load, upload-to-selected, run, clear,
 * plus a small live stats card. Companion: WaypointPanel (right pane).
 */
export function WaypointActionDock() {
  const name = useMission((s) => s.name);
  const waypoints = useMission((s) => s.waypoints);
  const clear = useMission((s) => s.clear);
  const loadFromJSON = useMission((s) => s.loadFromJSON);
  const toJSON = useMission((s) => s.toJSON);
  const selection = useSelection((s) => s.selected);
  const fileRef = useRef<HTMLInputElement>(null);

  const targetIds = Array.from(selection);
  const canUpload = waypoints.length > 0 && targetIds.length > 0;

  // Progress for uploads to the currently-targeted robots only. We don't show
  // uploads to other robots here — those would surface via the Toaster only.
  const pending = useMissionUpload((s) => s.pending);
  const targetSet = useMemo(() => new Set(targetIds), [targetIds.join(',')]);
  const myUploads = useMemo(
    () => Object.values(pending).filter((u) => targetSet.has(u.robotId)),
    [pending, targetSet],
  );
  const inFlightCount = myUploads.filter(
    (u) => u.status === 'pending' || u.status === 'accepted' || u.status === 'in_progress',
  ).length;
  const completedCount = myUploads.length - inFlightCount;
  const busy = inFlightCount > 0;

  const stats = useMemo(() => {
    let len = 0;
    for (let i = 1; i < waypoints.length; i++) {
      len += distM(waypoints[i - 1]!, waypoints[i]!);
    }
    // Assume 8 m/s nominal until we surface per-wp speed.
    const etaS = Math.round(len / 8);
    return { count: waypoints.length, lengthM: Math.round(len), etaS };
  }, [waypoints]);

  function toCommand(w: Waypoint): Waypoint {
    return w;
  }

  function uploadToSelected() {
    if (!canUpload) return;
    const wp = waypoints.map(toCommand).map((w) => ({
      seq: w.seq,
      lat: w.lat,
      lon: w.lon,
      alt_m: w.alt_m,
      hold_s: w.hold_s ?? null,
      acceptance_radius_m: w.acceptance_radius_m ?? null,
      speed_m_s: w.speed_m_s ?? null,
      is_fly_through: w.is_fly_through ?? null,
      yaw_deg: w.yaw_deg ?? null,
      gimbal_pitch_deg: w.gimbal_pitch_deg ?? null,
      gimbal_yaw_deg: w.gimbal_yaw_deg ?? null,
      camera_action:
        w.camera_action && w.camera_action !== 'NONE'
          ? mapCameraAction(w.camera_action)
          : null,
      camera_photo_interval_s: w.camera_photo_interval_s ?? null,
      camera_photo_distance_m: w.camera_photo_distance_m ?? null,
      vehicle_action:
        w.vehicle_action && w.vehicle_action !== 'NONE'
          ? mapVehicleAction(w.vehicle_action)
          : null,
    }));
    for (const id of targetIds) {
      const cmd: Command = {
        kind: 'upload_mission',
        id: makeId(),
        robot_id: id,
        priority: 'normal',
        waypoints: wp,
      };
      // Track BEFORE send so an immediate ack can't race the tracker entry.
      useMissionUpload.getState().track({
        commandId: cmd.id,
        robotId: id,
        startedTs: Date.now(),
        waypointCount: waypoints.length,
      });
      sendCommand(cmd);
    }
    log.action(`Upload mission → ${targetIds.length} robot(s)`, {
      targets: targetIds,
      waypoints: waypoints.length,
    });
  }

  function runOnSelected() {
    for (const id of targetIds) {
      sendCommand({
        kind: 'start_mission',
        id: makeId(),
        robot_id: id,
        priority: 'normal',
      });
    }
    log.action(`Run mission → ${targetIds.length} robot(s)`, { targets: targetIds });
  }

  function exportFile(fmt: 'json' | 'kml' | 'geojson') {
    const safe = name.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'mission';
    if (fmt === 'json') downloadText(`${safe}.json`, toJSON(), 'application/json');
    else if (fmt === 'kml')
      downloadText(`${safe}.kml`, toKML(name, waypoints), 'application/vnd.google-earth.kml+xml');
    else downloadText(`${safe}.geojson`, toGeoJSON(name, waypoints), 'application/geo+json');
  }

  function importFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      try {
        if (file.name.toLowerCase().endsWith('.kml')) {
          loadFromJSON(JSON.stringify({ name: file.name.replace(/\.kml$/i, ''), waypoints: fromKML(text) }));
        } else if (file.name.toLowerCase().endsWith('.geojson')) {
          const parsed = fromGeoJSON(text);
          loadFromJSON(JSON.stringify({ name: file.name.replace(/\.geojson$/i, ''), waypoints: parsed }));
        } else {
          loadFromJSON(text);
        }
      } catch (e) {
        useToasts.getState().push({
          tone: 'critical',
          title: 'Mission import failed',
          body: (e as Error).message,
          durationMs: 8000,
        });
      }
    };
    reader.readAsText(file);
  }

  return (
    <div className="space-y-3.5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-1.5 rounded border border-border-subtle bg-surface-2/40 p-2 font-mono text-[10px]">
        <Stat label="PTS" value={String(stats.count)} />
        <Stat label="LEN" value={`${stats.lengthM}m`} />
        <Stat label="ETA" value={`${stats.etaS}s`} />
      </div>

      {/* Upload + Run */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[10px] text-fg-muted">
          <span>
            Target:{' '}
            <span className="font-mono text-fg-secondary">
              {targetIds.length === 0
                ? '—'
                : targetIds.length === 1
                  ? targetIds[0]
                  : `${targetIds.length} robots`}
            </span>
          </span>
        </div>
        <Button
          size="sm"
          variant="primary"
          disabled={!canUpload || busy}
          onClick={uploadToSelected}
        >
          {busy ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Uploading… {completedCount}/{myUploads.length}
            </>
          ) : (
            <>
              <Upload className="h-3.5 w-3.5" /> Upload to selected
            </>
          )}
        </Button>
        <div className="grid grid-cols-2 gap-1.5">
          <Button
            size="sm"
            variant="secondary"
            disabled={targetIds.length === 0}
            onClick={runOnSelected}
          >
            <Play className="h-3.5 w-3.5" /> Run
          </Button>
          <Button size="sm" variant="ghost" disabled={waypoints.length === 0} onClick={clear}>
            <Trash2 className="h-3.5 w-3.5" /> Clear
          </Button>
        </div>
      </div>

      {/* Save / Load */}
      <div className="space-y-1.5">
        <Label>Save / Load</Label>
        <div className="grid grid-cols-3 gap-1">
          <Button size="sm" variant="ghost" onClick={() => exportFile('json')}>
            <Download className="h-3 w-3" /> JSON
          </Button>
          <Button size="sm" variant="ghost" onClick={() => exportFile('kml')}>
            <Download className="h-3 w-3" /> KML
          </Button>
          <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()}>
            <FileUp className="h-3 w-3" /> Load
          </Button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".json,.kml,.geojson"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) importFile(f);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="block font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
      style={{ color: 'var(--color-accent)' }}
    >
      {children}
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[8px] uppercase tracking-[0.14em] text-fg-muted">{label}</span>
      <span className="text-[11px] tabular-nums text-fg-primary">{value}</span>
    </div>
  );
}
